import { getStandardizedPathname } from '../../features/analytics/segment'
import { preventAnalyticLoading } from '../../features/analytics/utils'
import { isAmplitudeEventEnabled } from './amplitudeEvents'
import { FwTrackingEvent } from './FwTrackingEvent'

export { FwTrackingEvent }

type AmplitudeSdk = typeof import('@amplitude/analytics-browser')

/**
 * Migration Segment → Amplitude, progressive (event par event).
 *
 * Routage dans `sendEvent` :
 *   - événement présent dans l'allowlist Amplitude  → envoyé à Amplitude
 *   - sinon (pas encore migré)                      → envoyé à Segment (inchangé)
 *
 * Tant qu'un événement n'est pas flagué dans `amplitudeEvents.ts`, il continue
 * de partir vers Segment exactement comme avant. Aucun événement n'est perdu.
 */
export class TrackingService {
  private static enabled = false

  // --- Amplitude (cible de la migration, chargé dynamiquement) ---
  private static amplitude: AmplitudeSdk | null = null
  private static amplitudeInitStarted = false
  /** Actions Amplitude émises avant la fin de l'import dynamique, rejouées une fois prêt. */
  private static pendingAmplitudeActions: Array<(amplitude: AmplitudeSdk) => void> = []

  public static initAmplitude(apiKey: string, userId?: string) {
    if (preventAnalyticLoading(document) || this.amplitudeInitStarted) {
      return
    }
    this.amplitudeInitStarted = true

    import('@amplitude/analytics-browser')
      .then((amplitude) => {
        amplitude.init(apiKey, userId, {
          autocapture: false,
          // Doit correspondre à la région du projet Amplitude (US par défaut).
          // EU uniquement si le projet est hébergé sur le data center européen.
          serverZone: 'US',
          // Par défaut Amplitude rejette les user_id/device_id de moins de 5
          // caractères. Nos trackingId peuvent être plus courts.
          minIdLength: 1,
        })
        this.amplitude = amplitude
        this.pendingAmplitudeActions.forEach((action) => action(amplitude))
        this.pendingAmplitudeActions = []
      })
      .catch((error) => {
        this.amplitudeInitStarted = false
        console.error("Erreur lors du chargement d'Amplitude:", error)
      })
  }

  /**
   * Exécute une action Amplitude dès que le SDK est prêt. Si l'import dynamique
   * n'est pas encore résolu, l'action est mise en file puis rejouée — évite de
   * perdre les premiers `setUser` / `sendEvent` (race au chargement).
   */
  private static runWhenAmplitudeReady(action: (amplitude: AmplitudeSdk) => void) {
    if (this.amplitude) {
      action(this.amplitude)
    } else {
      this.pendingAmplitudeActions.push(action)
    }
  }

  // --- Identité utilisateur (Segment + Satismeter + Amplitude) ---
  public static removeUser() {
    if (!preventAnalyticLoading(document)) {
      TrackingService.enabled = false
      window.analytics.reset()

      this.pendingAmplitudeActions = []
      this.runWhenAmplitudeReady((amplitude) => amplitude.reset())
    }
  }

  public static setUser(userId: string, name: string, email: string, userCreatedAt: Date) {
    if (!preventAnalyticLoading(document)) {
      TrackingService.enabled = true

      window.analytics.identify(userId, {
        name,
        email,
        appVersion: process.env.REACT_APP_VERSION,
      })
      window.satismeter({
        userId,
        traits: {
          name,
          email,
          createdAt: userCreatedAt,
          appVersion: process.env.REACT_APP_VERSION,
        },
      })

      this.runWhenAmplitudeReady((amplitude) => {
        amplitude.setUserId(userId)
        const identify = new amplitude.Identify()
          .set('name', name)
          .set('email', email)
          .set('appVersion', process.env.REACT_APP_VERSION ?? '')
          .set('createdAt', userCreatedAt.toISOString())
        amplitude.identify(identify)
      })

      TrackingService.page(window.location.pathname)
    }
  }

  /**
   * Définit une propriété utilisateur Amplitude (ex. `dictation_enabled`).
   * Permet de segmenter les utilisateurs côté Amplitude (cohortes), indépendamment
   * des événements — c'est ce qui rend adressable la population « a la feature mais
   * ne l'utilise pas ». Spécifique à Amplitude, pas d'équivalent Segment.
   */
  public static setUserProperty(key: string, value: string | number | boolean) {
    if (preventAnalyticLoading(document)) {
      return
    }
    this.runWhenAmplitudeReady((amplitude) => {
      const identify = new amplitude.Identify().set(key, value)
      amplitude.identify(identify)
    })
  }

  // --- Dispatch d'événement : Amplitude si migré, sinon Segment ---
  public static sendEvent(event: FwTrackingEvent, options: object = {}) {
    if (!TrackingService.enabled || preventAnalyticLoading(document)) {
      return
    }

    // Événement migré → Amplitude.
    if (isAmplitudeEventEnabled(event)) {
      this.runWhenAmplitudeReady((amplitude) => {
        try {
          amplitude.track(event, {
            ...options,
            timestamp: new Date().toISOString(),
            appVersion: process.env.REACT_APP_VERSION,
          })
        } catch (error) {
          console.error("Erreur lors de l'envoi de l'événement Amplitude:", error)
        }
      })
      return
    }

    // Pas encore migré → Segment (comportement d'origine).
    if (!window.analytics) {
      throw new Error('Initialize Segment first')
    }
    try {
      window.analytics.track(event, options)
    } catch (error) {
      console.error(error)
    }
  }

  public static page(pathname: string) {
    if (TrackingService.enabled) {
      if (preventAnalyticLoading(document)) {
        return
      }
      window.analytics.page(getStandardizedPathname(pathname))
    }
  }
}
